<script setup>
import { computed, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import { useLanguageStore } from "@/stores/languageStore.js";
import { useSettingsStore } from "@/stores/settingsStore.js";
import { LEGAL_NAV } from "@/data/legalLocales.js";

const router = useRouter();
const languageStore = useLanguageStore();
const settingsStore = useSettingsStore();
const legalNav = computed(() => LEGAL_NAV[languageStore.language] || LEGAL_NAV.en);

const difficultyOptions = ["standard", "hard", "extreme"];
const speedOptions = ["slow", "normal", "fast"];

const ready = ref(false);

onMounted(async () => {
  try {
    await settingsStore.refresh();
    ready.value = true;
  } catch (error) {
    console.error("Failed to load game settings.", error);
  }
});

async function updateSetting(patch) {
  if (
    !ready.value ||
    settingsStore.loading ||
    settingsStore.saving
  ) {
    return;
  }

  try {
    await settingsStore.update(patch);
  } catch (error) {
    console.error("Failed to update game settings.", error);
  }
}

function saveAndBack() {
  if (settingsStore.saving) {
    return;
  }

  router.push({ name: "home" });
}
</script>

<template>
  <section class="settings page-card">
    <div class="settings-topbar">
      <button class="header-back" type="button" @click="router.push({ name: 'home' })">‹</button>
      <h1>{{ languageStore.t('settingsTitle') }}</h1>
      <span class="topbar-spacer" />
    </div>

    <div class="setting-panel">
      <strong class="setting-label">{{ languageStore.t('difficulty') }}</strong>
      <div class="segment-row three">
        <button
          v-for="value in difficultyOptions"
          :key="value"
          class="segment-button"
          :class="{ active: settingsStore.settings.difficulty === value }"
          type="button"
          :disabled="!ready || settingsStore.loading || settingsStore.saving"
          @click="updateSetting({ difficulty: value })"
        >
          {{ languageStore.t(value) }}
        </button>
      </div>
    </div>

    <div class="setting-panel">
      <strong class="setting-label">{{ languageStore.t('sound') }}</strong>
      <div class="segment-row two">
        <button
          class="segment-button"
          :class="{ active: settingsStore.settings.soundEnabled }"
          type="button"
          :disabled="!ready || settingsStore.loading || settingsStore.saving"
          @click="updateSetting({ soundEnabled: true })"
        >{{ languageStore.t('on') }}</button>
        <button
          class="segment-button"
          :class="{ active: !settingsStore.settings.soundEnabled }"
          type="button"
          :disabled="!ready || settingsStore.loading || settingsStore.saving"
          @click="updateSetting({ soundEnabled: false })"
        >{{ languageStore.t('off') }}</button>
      </div>
    </div>

    <div class="setting-panel">
      <strong class="setting-label">{{ languageStore.t('ballSpeed') }}</strong>
      <div class="segment-row three">
        <button
          v-for="value in speedOptions"
          :key="value"
          class="segment-button"
          :class="{ active: settingsStore.settings.ballSpeedPreset === value }"
          type="button"
          :disabled="!ready || settingsStore.loading || settingsStore.saving"
          @click="updateSetting({ ballSpeedPreset: value })"
        >
          {{ languageStore.t(value) }}
        </button>
      </div>
    </div>

    <div class="mode-guide">
      <strong>{{ languageStore.t('modeGuide') }}</strong>
      <ul>
        <li>{{ languageStore.t('modeGuideStandard') }}</li>
        <li>{{ languageStore.t('modeGuideHard') }}</li>
        <li>{{ languageStore.t('modeGuideExtreme') }}</li>
        <li class="muted-line">{{ languageStore.t('modeGuideSpeed') }}</li>
      </ul>
    </div>

    <div class="legal-panel">
      <strong class="setting-label">{{ legalNav[0] }}</strong>
      <button type="button" @click="router.push({ name:'legal', params:{ document:'privacy' } })">
        {{ legalNav[1] }}
      </button>
      <button type="button" @click="router.push({ name:'legal', params:{ document:'terms' } })">
        {{ legalNav[2] }}
      </button>
      <button type="button" @click="router.push({ name:'legal', params:{ document:'community' } })">
        {{ legalNav[3] }}
      </button>
    </div>

    <button
      class="save-button"
      type="button"
      :disabled="settingsStore.loading || settingsStore.saving"
      @click="saveAndBack"
    >
      {{ languageStore.t('saveAndBack') }}
    </button>
  </section>
</template>

<style scoped>
.settings {
  width: min(100%, 660px);
  min-height: 690px;
  margin: 0 auto;
  padding: 20px;
  display: grid;
  align-content: start;
  gap: 16px;
}

.settings-topbar {
  min-height: 54px;
  display: grid;
  grid-template-columns: 48px minmax(0,1fr) 48px;
  align-items: center;
  gap: 12px;
  padding: 6px 0;
}

.settings-topbar h1 {
  margin: 0;
  text-align: center;
  font-size: clamp(24px, 4.6vw, 30px);
  font-weight: 950;
  letter-spacing: .02em;
}

.header-back {
  width: 44px;
  height: 40px;
  border: 1px solid rgba(82,246,232,.42);
  border-radius: 999px;
  color: #fff;
  background: rgba(5,31,49,.72);
  font-size: 31px;
  line-height: 1;
}

.setting-panel {
  min-height: 98px;
  padding: 17px 18px 16px;
  border: 1px solid rgba(82,246,232,.28);
  border-radius: 19px;
  background: rgba(5,28,47,.78);
  box-shadow: inset 0 0 0 1px rgba(255,255,255,.02);
}

.setting-label {
  display: block;
  margin: 0 0 12px 2px;
  font-size: 16px;
  font-weight: 900;
}

.segment-row {
  display: grid;
  gap: 9px;
}

.segment-row.three { grid-template-columns: repeat(3, minmax(0,1fr)); }
.segment-row.two { grid-template-columns: repeat(2, minmax(0,1fr)); }

.segment-button {
  min-width: 0;
  min-height: 42px;
  padding: 9px 10px;
  border: 1px solid rgba(82,246,232,.28);
  border-radius: 999px;
  color: rgba(255,255,255,.84);
  background: rgba(5,31,49,.75);
  font-size: 14px;
  font-weight: 850;
}

.segment-button.active {
  color: #fff;
  border-color: #52f6e8;
  background: linear-gradient(180deg, rgba(24,109,119,.80), rgba(9,67,82,.86));
  box-shadow: 0 0 16px rgba(82,246,232,.16);
}

.mode-guide {
  min-height: 152px;
  padding: 18px 19px;
  border: 1px solid rgba(75,188,255,.38);
  border-radius: 20px;
  background: rgba(3,20,36,.82);
}

.mode-guide strong { font-size: 16px; }
.mode-guide ul {
  margin: 13px 0 0;
  padding-left: 21px;
  display: grid;
  gap: 9px;
  color: rgba(255,255,255,.86);
  font-size: 13.5px;
  line-height: 1.55;
}
.mode-guide .muted-line { color: rgba(255,255,255,.62); }

.legal-panel { padding:17px 18px; border:1px solid rgba(82,246,232,.28); border-radius:19px; background:rgba(5,28,47,.78); display:grid; gap:9px; }
.legal-panel button { min-height:42px; padding:9px 14px; border:1px solid rgba(82,246,232,.28); border-radius:12px; color:#effffd; background:rgba(5,31,49,.75); text-align:left; font-weight:800; }

.save-button {
  width: min(100%, 310px);
  min-height: 50px;
  margin: 2px auto 4px;
  border: 1px solid rgba(82,246,232,.78);
  border-radius: 999px;
  color: #fff;
  background: linear-gradient(180deg, rgba(18,95,110,.94), rgba(4,48,65,.94));
  box-shadow: 0 0 19px rgba(82,246,232,.15);
  font-size: 16px;
  font-weight: 900;
}

@media (max-width: 560px) {
  .settings {
    width: 100%;
    min-height: calc(100dvh - 28px);
    padding: 15px;
    gap: 13px;
  }
  .settings-topbar { min-height: 64px; }
  .settings-topbar h1 { font-size: 24px; }
  .setting-panel { min-height: 92px; padding: 15px 14px; }
  .setting-label { font-size: 15px; margin-bottom: 10px; }
  .segment-button { min-height: 40px; font-size: 13px; padding-inline: 6px; }
  .mode-guide { min-height: 145px; padding: 16px; }
  .mode-guide strong { font-size: 15px; }
  .mode-guide ul { font-size: 12.5px; gap: 8px; }
  .save-button { min-height: 48px; font-size: 15px; }
}

@media (max-width: 380px) {
  .settings { padding: 12px; gap: 11px; }
  .setting-panel { padding: 13px 10px; }
  .segment-button { font-size: 12px; }
  .mode-guide { padding: 14px 12px; }
  .mode-guide ul { font-size: 11.8px; }
}
</style>
